import {
  ChangeDetectionStrategy,
  Component,
  HostListener, Input, OnInit
} from '@angular/core';
import { Subject, map } from 'rxjs';
import { getDocument, getWindow } from 'ssr-window';

interface IImage {
  selector: string;
  backgroundImage?: string;
  backgroundColor?: string;
}
interface IImageWithDOMRect extends IImage {
  rect: DOMRect;
}
interface IImageWithStats extends IImageWithDOMRect {
  isBelowScreen: boolean;
  isAboveScreen: boolean;
  isVisible: boolean;
  top: number;
  bottom: number;
  percentageOfScreenFilled: number;
}

type ILayerWithStyle = { [key: string]: any };
@Component({
  selector: 'app-background-scroll-animator',
  templateUrl: './background-scroll-animator.component.html',
  styleUrls: ['./background-scroll-animator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BackgroundScrollAnimatorComponent implements OnInit {
  @Input() images: IImage[] = [
    {
      selector: '.app-customer-demand',
      backgroundColor: ' rgb(23, 122, 244, .89)',
      backgroundImage: 'assets/images/integration-transformation-inovation.jpg',
    },
    {
      selector: '.app-web-based-service-integration',
      backgroundColor: ' rgb(23, 122, 244, .89)',
      backgroundImage: '/assets/images/integration-1.jpg',
    },
    {
      selector: '.app-web-based-service-transformation',
      backgroundColor: ' rgb(23, 122, 244, .89)',
      backgroundImage: '/assets/images/transformation.jpg',
    },
    {
      selector: '.app-web-based-service-innovation',
      backgroundColor: ' rgb(23, 122, 244, .89)',
      backgroundImage: '/assets/images/innovation.jpg',
    },
    {
      selector: '.app-technical',
     // backgroundColor:
      //  'linear-gradient(180deg, rgb(13, 120, 29, .89) 0%, rgb(13, 120, 29, .89) 100%)',
      //backgroundImage: '/assets/images/letsgettechnical.jpg',
    },
    {
      selector: '.app-sitemule-overview',
      //backgroundColor: '#fff',
    },
    {
      selector: '.app-persons',
      //backgroundColor:
      //  'linear-gradient(180deg, rgba(4, 22, 45, .98)0%, rgba(4, 22, 45, .98) 100%)',
      //backgroundImage: '/assets/images/integration.jpg',
    },
    {
      selector: '.app-write-us',
      // backgroundColor:
      //   'linear-gradient(180deg, rgba(255, 255, 255, 1)0%, rgba(255, 255, 255, 1) 100%)',
    },
  ];

  refresher$ = new Subject();

  layers$ = this.refresher$.pipe(
    map(() => {
      return this.images
        .map((image): IImageWithDOMRect | undefined => {
          const win = getWindow();
          const rect = getDocument()
            .querySelector(image.selector)
            ?.getBoundingClientRect();
          if (rect) {
            return {
              ...image,
              rect,
            };
          }
          return undefined;
        })
        .filter(
          (item: IImageWithDOMRect | undefined): item is IImageWithDOMRect => {
            return !!item;
          }
        )
        .sort((a, b) => {
          if (a.rect.top > b.rect.top) {
            return 1;
          }
          if (b.rect.top > a.rect.top) {
            return -1;
          }
          return 0;
        });
    }),
    map((imagesSortedByDOMSequence) => {
      const win = getWindow();
      const windowHeight = win?.innerHeight || 0;
      return imagesSortedByDOMSequence.map((image, index): IImageWithStats => {
        const el = image.rect;
        const top = el.top;
        const bottom = el.bottom;
        const isBelowScreen = top > windowHeight;
        const isAboveScreen = bottom < 0;
        const isTopInView = top >= 0 && top < windowHeight;
        const isVisible = !isBelowScreen && !isAboveScreen;
        const percentageOfScreenFilled = (() => {
          if (isTopInView) {
            return ((windowHeight - top) / windowHeight) * 100;
          }

          return 0;
        })();
        return {
          ...image,
          top,
          bottom,
          isBelowScreen,
          isAboveScreen,
          isVisible,
          percentageOfScreenFilled,
        };
      });
    }),
    map((imagesWithStatsSortedByDOMSequence): Array<ILayerWithStyle> => {
      const startAnimationPercentage = 25;
      const endAnimationPercentage = 90;
      const actualIndex = imagesWithStatsSortedByDOMSequence.findIndex(
        ({ isVisible }) => isVisible
      );
      const currentImage = imagesWithStatsSortedByDOMSequence[actualIndex];
      const nextImage = imagesWithStatsSortedByDOMSequence[actualIndex + 1];

      const layers: ILayerWithStyle[] = [];

      if(!currentImage) {
        return layers;
      }

      if (
        !nextImage ||
        nextImage.percentageOfScreenFilled <= startAnimationPercentage
      ) {
        if (currentImage.backgroundImage) {
          layers.push({
            background: `url('${currentImage.backgroundImage}')`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          });
        }
        if (currentImage.backgroundColor) {
          layers.push({
            background: currentImage.backgroundColor,
          });
        }

        return layers;
      }

      if (nextImage.percentageOfScreenFilled >= endAnimationPercentage) {
        if (nextImage.backgroundImage) {
          layers.push({
            background: `url('${nextImage.backgroundImage}')`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          });
        }
        if (nextImage.backgroundColor) {
          layers.push({
            background: nextImage.backgroundColor,
          });
        }

        return layers;
      }

      const animationPercentageNextImage =
        ((nextImage.percentageOfScreenFilled - startAnimationPercentage) /
          (endAnimationPercentage - startAnimationPercentage)) *
        100;
      if (
        currentImage.backgroundImage &&
        currentImage.backgroundImage !== nextImage.backgroundImage
      ) {
        layers.push({
          background: `url('${currentImage.backgroundImage}')`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          filter: `blur(${(animationPercentageNextImage / 100) * 40}px)`,
        });
      }
      if (
        currentImage.backgroundColor &&
        currentImage.backgroundColor !== nextImage.backgroundColor
      ) {
        layers.push({
          background: currentImage.backgroundColor,
        });
      }
      if (nextImage.backgroundImage) {
        layers.push({
          background: `url('${nextImage.backgroundImage}')`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: (() => {
            if (currentImage.backgroundImage === nextImage.backgroundImage) {
              return 1;
            }
            return animationPercentageNextImage / 100;
          })(),
        });
      }
      if (nextImage.backgroundColor) {
        layers.push({
          background: nextImage.backgroundColor,
          opacity: (() => {
            if (currentImage.backgroundColor === nextImage.backgroundColor) {
              return 1;
            }
            return animationPercentageNextImage / 100;
          })(),
        });
      }

      return layers;
    })
  );

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    this.refresher$.next(undefined);
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.refresher$.next(undefined);
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.refresher$.next(undefined);
    });
  }
}
